import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Computer,
  Schedule,
  Assignment,
  CheckCircle,
} from '@mui/icons-material';
import { format, addDays } from 'date-fns';
import { ru } from 'date-fns/locale';

import apiClient from '../../services/api';
import type { Playlist, Device, DeviceStatus, ScheduleEventCreate, Schedule as ScheduleType } from '../../types';
import { invalidatePlaylistData, invalidateScheduleData } from '../../utils/cacheInvalidation';

interface PlaylistAssignDevicesDialogProps {
  open: boolean;
  playlist: Playlist | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const PlaylistAssignDevicesDialog: React.FC<PlaylistAssignDevicesDialogProps> = ({
  open,
  playlist,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedDevices, setSelectedDevices] = useState<number[]>([]);
  const [scheduleName, setScheduleName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Получаем список доступных устройств
  const { data: devicesResponse, isLoading: loadingDevices } = useQuery({
    queryKey: ['devices'],
    queryFn: () => apiClient.getDevices(),
    enabled: open,
  });

  const devices = devicesResponse?.items || [];

  // Устанавливаем значения по умолчанию при открытии диалога
  useEffect(() => {
    if (open && playlist) {
      const now = new Date();
      const defaultStart = format(now, "yyyy-MM-dd'T'HH:mm");
      const defaultEnd = format(addDays(now, 1), "yyyy-MM-dd'T'HH:mm");

      setScheduleName(`${playlist.name} - Назначение устройств`);
      setStartTime(defaultStart);
      setEndTime(defaultEnd);
      setSelectedDevices([]);
      setError(null);
    }
  }, [open, playlist]);

  const handleDeviceToggle = (deviceId: number) => {
    setSelectedDevices(prev =>
      prev.includes(deviceId)
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAll = () => {
    const onlineDevices = devices.filter(device => device.status === 'online').map(device => device.id);
    setSelectedDevices(onlineDevices);
  };

  const handleDeselectAll = () => {
    setSelectedDevices([]);
  };

  const handleSubmit = async () => {
    if (!playlist || selectedDevices.length === 0) {
      setError('Выберите хотя бы одно устройство');
      return;
    }

    if (!startTime || !endTime) {
      setError('Укажите время начала и окончания');
      return;
    }

    if (new Date(startTime) >= new Date(endTime)) {
      setError('Время окончания должно быть позже времени начала');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Создаем расписание
      const normalizedStartTime = new Date(startTime).toISOString();
      const normalizedEndTime = new Date(endTime).toISOString();

      const scheduleData: any = {
        name: scheduleName || `${playlist.name} - Назначение устройств`,
        description: `Автоматически созданное расписание для плейлиста "${playlist.name}"`,
        schedule_type: 'one_time',
        start_date: normalizedStartTime,
        end_date: normalizedEndTime,
        is_active: true,
      };

      const schedule = await apiClient.createSchedule(scheduleData);

      // Создаем события для каждого выбранного устройства
      const eventPromises = selectedDevices.map(deviceId => {
        const eventData: ScheduleEventCreate = {
          schedule_id: schedule.id, // Добавляем обратно для валидации Pydantic
          name: `${playlist.name} на устройстве ${deviceId}`,
          description: `Воспроизведение плейлиста "${playlist.name}"`,
          start_time: normalizedStartTime,
          end_time: normalizedEndTime,
          priority: 10, // HIGH priority (Priority.HIGH = 10)
          device_id: deviceId,
          device_group_id: null,
          playlist_id: playlist.id,
          media_id: null,
          playback_settings: null,
        };

        // Отладочное логирование (можно убрать после тестирования)
        console.log('🔍 Отправляемые данные события:', eventData);

        return apiClient.createScheduleEventForSchedule(schedule.id, eventData);
      });

      await Promise.all(eventPromises);

      await Promise.all([
        invalidatePlaylistData(queryClient),
        invalidateScheduleData(queryClient),
      ]);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error('Ошибка назначения устройств:', err);
      setError(err.response?.data?.detail || 'Произошла ошибка при назначении устройств');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDeviceStatusColor = (status: DeviceStatus) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'default';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getDeviceStatusText = (status: DeviceStatus) => {
    switch (status) {
      case 'online': return 'Онлайн';
      case 'offline': return 'Офлайн';
      case 'error': return 'Ошибка';
      default: return status;
    }
  };

  if (!playlist) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Assignment />
          <Typography variant="h6">
            Назначение устройств плейлисту
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Плейлист: <strong>{playlist.name}</strong>
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Настройки расписания */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
            Параметры расписания
          </Typography>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr', mb: 2 }}>
            <TextField
              label="Время начала"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Время окончания"
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>

          <TextField
            label="Название расписания"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            fullWidth
            placeholder={`${playlist.name} - Назначение устройств`}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Выбор устройств */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">
              <Computer sx={{ mr: 1, verticalAlign: 'middle' }} />
              Выбор устройств ({selectedDevices.length} из {devices.length})
            </Typography>
            <Box>
              <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
                Выбрать все онлайн
              </Button>
              <Button size="small" onClick={handleDeselectAll}>
                Отменить выбор
              </Button>
            </Box>
          </Box>

          {loadingDevices ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {devices.map((device) => (
                <ListItem
                  key={device.id}
                  onClick={() => handleDeviceToggle(device.id)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedDevices.includes(device.id)}
                      disabled={device.status !== 'online'}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={device.name}
                    secondary={`${device.location || 'Местоположение не указано'} • ${getDeviceStatusText(device.status)}`}
                  />
                  <Box sx={{ ml: 1 }}>
                    <Chip
                      label={getDeviceStatusText(device.status)}
                      color={getDeviceStatusColor(device.status)}
                      size="small"
                    />
                  </Box>
                  <ListItemSecondaryAction>
                    {selectedDevices.includes(device.id) && (
                      <CheckCircle color="success" />
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {devices.length === 0 && !loadingDevices && (
            <Typography color="text.secondary" align="center" py={3}>
              Нет доступных устройств
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || selectedDevices.length === 0}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <Assignment />}
        >
          {isSubmitting
            ? 'Назначение...'
            : `Назначить на ${selectedDevices.length} ${selectedDevices.length === 1 ? 'устройство' : 'устройств'}`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaylistAssignDevicesDialog;
